/**
 * Waiting on my approval (Aziz 2026-09-29, for Mohannad): every review, every rough
 * cut or colouring and every proposed shoot that waits on this person's decision, under their own name,
 * oldest first, with who asked and since when. One tap opens the creative's decision.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stamp, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';
import { fetchWaitingOnMe } from '@/lib/queries';
import { useAuth } from '@/hooks/use-auth';
import { bidi, BIDI_CLS } from '@/lib/bidi';

function ago(d?: string | null): string {
  if (!d) return '';
  const h = (Date.now() - new Date(d).getTime()) / 36e5;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${Math.round(h)} h`;
  return `${Math.round(h / 24)} days`;
}

export default function WaitingOnMe() {
  const { user } = useAuth();
  const q = useQuery({ queryKey: ['waiting-on-me'], queryFn: fetchWaitingOnMe, refetchInterval: 60_000 });
  const rows = q.data ?? [];
  if (q.isLoading) return null;
  const first = (user?.display_name ?? '').split(' ')[0];

  return (
    <Card className="mb-5" data-testid="waiting-on-me">
      <div className="flex items-center gap-2 mb-2">
        <Stamp size={14} className="text-accent-400" />
        <h2 className="text-sm font-semibold text-muted-strong">Waiting on {first ? `${first}'s` : 'my'} approval</h2>
        <span className={cn('text-[11px] font-semibold px-1.5 py-0.5 rounded-full',
          rows.length ? 'bg-accent-500 text-white' : 'bg-slate-800 text-muted')} data-testid="waiting-count">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted">Nothing is waiting on your decision.</div>
      ) : (
        <ul className="divide-y divide-slate-800">
          {rows.map((r) => {
            const hours = r.since ? (Date.now() - new Date(r.since).getTime()) / 36e5 : 0;
            return (
              <li key={`${r.kind}-${r.creative_id}-${r.what}`}>
                <Link to={r.link || `/creatives/${r.creative_id}`} data-testid="waiting-row"
                  className="flex items-center gap-3 py-2.5 hover:bg-white/[0.03] rounded-md px-1 -mx-1">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-accent-600/20 text-accent-400">{r.what}</span>
                      {r.revision_round > 0 && <span className="text-[10px] text-amber-300">round {r.revision_round + 1}</span>}
                      <span className="text-[11px] text-muted">{r.creative_code} · {r.clinic}</span>
                    </div>
                    <div dir="auto" className={cn('text-sm text-muted-strong truncate mt-0.5 text-left', BIDI_CLS)}>{bidi(r.creative_title)}</div>
                    <div className="text-[11px] text-muted mt-0.5">
                      {r.asked_by ? `Asked by ${r.asked_by}` : 'Asked'} · waiting <span className={cn(hours > 24 && 'text-amber-300')}>{ago(r.since)}</span>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-accent-400 shrink-0">Review <ChevronRight size={14} /></span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
